import SEO, { breadcrumbSchema, faqSchema } from "../components/SEO";
import PageHero from "../components/PageHero";
import CtaBand from "../components/CtaBand";
import FaqAccordion from "../components/FaqAccordion";
import BookingWidget from "../components/BookingWidget";
import { RouteCard, SectionHead } from "../components/Cards";
import Reveal from "../components/Reveal";
import { ROUTES, ALL_IMAGES } from "../data/content";
import { Check } from "lucide-react";

const FAQS = [
  { q: "What is a one-way taxi service?", a: "You book a cab for a single journey — for example Jodhpur to Jaipur — and pay only for that side. You don't pay for the vehicle's return." },
  { q: "Which one-way routes do you cover from Jodhpur?", a: "Jaipur, Udaipur, Jaisalmer, Mount Abu, Ajmer, Pushkar, Bikaner, Delhi, Osian and most other Rajasthan cities. If your route isn't listed, ask on WhatsApp." },
  { q: "How do I get the fare for my route?", a: "Tap 'Get Latest Fare' on any route card or message us your route and date on WhatsApp. We quote the current fare — no fixed outdated price lists." },
  { q: "Can I add a stop on the way?", a: "Yes, sightseeing halts like Ranakpur (towards Udaipur) or Pushkar (towards Jaipur) can be added. Mention it while booking so we plan the timing and quote accordingly." },
];

export default function OneWay() {
  return (
    <>
      <SEO
        title="One Way Taxi Service in Rajasthan — From Jodhpur"
        description="One way taxi from Jodhpur to Jaipur, Udaipur, Jaisalmer, Mount Abu, Ajmer, Pushkar, Bikaner & Delhi. Pay one side only. Get the latest fare on WhatsApp — Blue City Taxi."
        path="/one-way-taxi"
        schemas={[
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "One Way Taxi", path: "/one-way-taxi" }]),
          faqSchema(FAQS),
        ]}
      />
      <PageHero
        title="One Way Taxi Service in Rajasthan"
        subtitle="Why pay for a round trip when you're only going one way? Book a one-way drop from Jodhpur to any major Rajasthan city — and to Delhi — in a clean AC cab."
        image={ALL_IMAGES.roadTaxi}
        crumbs={[{ label: "Home", to: "/" }, { label: "One Way Taxi" }]}
      />
      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <SectionHead center={false} eyebrow="How It Works" title="One-Way Booking, Explained Simply" />
          <Reveal className="mt-8 space-y-4">
            {[
              ["Tell us your route & date", "WhatsApp or call with your pickup, drop city and travel date."],
              ["Get the latest fare", "We share the current one-way fare for your vehicle choice — no outdated fixed price lists."],
              ["Confirm & travel", "Your driver arrives on time, helps with luggage, and drops you at your exact address."],
            ].map(([t, d], i) => (
              <div key={t} className="flex gap-4 bg-white rounded-2xl border border-slate-200/80 p-5">
                <span className="w-9 h-9 shrink-0 rounded-full bg-royal text-white font-heading font-bold flex items-center justify-center">{i + 1}</span>
                <div>
                  <h3 className="font-heading font-bold text-navy">{t}</h3>
                  <p className="text-sm text-slate-600 mt-1">{d}</p>
                </div>
              </div>
            ))}
          </Reveal>
          <Reveal className="mt-8 bg-royal-50/70 rounded-2xl p-6">
            <h3 className="font-heading font-bold text-navy">Every one-way trip includes</h3>
            <ul className="mt-3 grid sm:grid-cols-2 gap-2">
              {["Pay for one side only", "Experienced highway drivers", "Door-to-door drop", "Luggage assistance", "Planned refreshment halts", "Sedan, SUV & tempo options"].map((b) => (
                <li key={b} className="flex items-center gap-2 text-sm text-slate-700"><Check size={14} className="text-gold-dark" /> {b}</li>
              ))}
            </ul>
          </Reveal>
        </div>
        <div className="lg:sticky lg:top-28 h-fit">
          <BookingWidget compact title="Get One-Way Fare" />
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 pb-8">
        <SectionHead eyebrow="Popular Routes" title="One-Way Taxi Routes From Jodhpur" />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ROUTES.map((r, i) => <RouteCard key={r.to} r={r} i={i} />)}
        </div>
      </section>
      <section className="max-w-3xl mx-auto px-6 py-16">
        <SectionHead eyebrow="Questions" title="One-Way Taxi FAQs" />
        <div className="mt-8"><FaqAccordion faqs={FAQS} idPrefix="oneway-faq" /></div>
      </section>
      <CtaBand title="Route not listed? We still go there." text="Message us any Rajasthan route on WhatsApp — if a road goes there, so do we." />
    </>
  );
}
